import { AccountType } from "@common/constants/domains/accounting/accounts";
import { parsePlainIntegerCodeText } from "@common/helpers/accounting";

// Longer than this, `n + 1` is past Number.MAX_SAFE_INTEGER and String() is
// exponential notation, which is not a usable code.
const MAX_SEED_DIGITS = 15;

export interface SuggestNextAccountCodeAccount {
    entityId: number;
    codeText: string | null;
    type: AccountType;
    parentGroupId: string | null;
}

export interface SuggestNextAccountCodeTarget {
    entityId: number;
    parentGroupId: string | null;
}

interface SuggestNextAccountCodeParams {
    accounts: SuggestNextAccountCodeAccount[];
    targets: SuggestNextAccountCodeTarget[];
    type: AccountType;
}

const targetPairKey = (
    entityId: number,
    parentGroupId: string | null,
): string => JSON.stringify([entityId, parentGroupId]);

const seedInteger = (codeText: string | null): number | null => {
    if (codeText === null) {
        return null;
    }
    if (codeText.trim().length > MAX_SEED_DIGITS) {
        return null;
    }
    return parsePlainIntegerCodeText(codeText);
};

export function suggestNextAccountCodeText({
    accounts,
    targets,
    type,
}: SuggestNextAccountCodeParams): string | null {
    if (targets.length === 0) {
        return null;
    }

    const targetEntityIds = new Set(targets.map((target) => target.entityId));
    const targetPairKeys = new Set(
        targets.map((target) =>
            targetPairKey(target.entityId, target.parentGroupId),
        ),
    );

    const inTargets = accounts.filter((account) =>
        targetEntityIds.has(account.entityId),
    );
    const inTargetsOfType = inTargets.filter(
        (account) => account.type === type,
    );

    const groupSeeds: number[] = [];
    const typeSeeds: number[] = [];
    for (const account of inTargetsOfType) {
        const seed = seedInteger(account.codeText);
        if (seed === null) {
            continue;
        }
        typeSeeds.push(seed);
        if (
            targetPairKeys.has(
                targetPairKey(account.entityId, account.parentGroupId),
            )
        ) {
            groupSeeds.push(seed);
        }
    }

    const seeds = groupSeeds.length > 0 ? groupSeeds : typeSeeds;
    if (seeds.length === 0) {
        return null;
    }

    const takenCodeTexts = new Set<string>();
    for (const account of inTargets) {
        if (account.codeText !== null) {
            takenCodeTexts.add(account.codeText.trim());
        }
    }

    let candidate = Math.max(...seeds) + 1;
    while (takenCodeTexts.has(String(candidate))) {
        candidate += 1;
    }
    return String(candidate);
}
